// API configuration and base client
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { interceptRequest } from '../../config/performance';
import { debugLogger } from '../../components/debug/DataLoadingDebugger';
import { handleAPIError, checkProductionConfig } from '../../utils/apiErrorHandler';
import { authEvents } from '../../utils/authEvents';

// Ensure HTTPS in production
const getAPIBaseURL = () => {
  const envURL = import.meta.env.VITE_API_BASE_URL;
  const isDev = import.meta.env.DEV;
  const isProd = import.meta.env.PROD;
  const mode = import.meta.env.MODE;
  const isIntegration = import.meta.env.VITE_INTEGRATION_MODE === 'true' || import.meta.env.VITE_ENVIRONMENT === 'integration';
  
  // Default URLs based on environment
  const defaultDevURL = 'http://localhost:8000';
  const defaultProdURL = 'https://app.univherse.com';
  
  console.log('🔍 Environment variables check:', {
    VITE_API_BASE_URL: envURL,
    PROD: isProd,
    DEV: isDev,
    MODE: mode,
    INTEGRATION: isIntegration,
    defaultForEnv: isDev ? defaultDevURL : defaultProdURL
  });
  
  // If we have an env URL, use it with production safety
  if (envURL) {
    console.log('✅ Using environment URL:', envURL);
    // Only convert to HTTPS in production if NOT in integration mode
    if (isProd && envURL.startsWith('http://') && !isIntegration) {
      console.warn('⚠️ Converting HTTP to HTTPS for production:', envURL);
      return envURL.replace('http://', 'https://');
    }
    if (isIntegration && envURL.startsWith('http://')) {
      console.log('🔧 Integration mode: keeping HTTP for local backend');
    }
    return envURL;
  }
  
  // Fallback based on environment
  const fallbackURL = isDev ? defaultDevURL : defaultProdURL;
  console.warn(`⚠️ No VITE_API_BASE_URL found, using ${mode} default:`, fallbackURL);
  return fallbackURL;
};

const API_BASE_URL = getAPIBaseURL();

// Additional safety check - force HTTPS in production environment (but respect integration mode)
const isIntegration = import.meta.env.VITE_INTEGRATION_MODE === 'true' || import.meta.env.VITE_ENVIRONMENT === 'integration';
const SAFE_API_BASE_URL = import.meta.env.PROD && !API_BASE_URL.startsWith('https://') && !isIntegration
  ? API_BASE_URL.replace(/^http:/, 'https:')
  : API_BASE_URL;

// Export for direct fetch usage in components
export { SAFE_API_BASE_URL as API_BASE_URL };

console.log('🌐 API Base URL:', SAFE_API_BASE_URL);
console.log('🔧 Environment:', {
  PROD: import.meta.env.PROD,
  DEV: import.meta.env.DEV,
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  protocol: window.location.protocol,
  host: window.location.host
});

// Check production configuration on initialization
if (import.meta.env.PROD) {
  checkProductionConfig();
}

// Create axios instance with default configuration
const createAPIClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: SAFE_API_BASE_URL,
    timeout: 120000, // 2 minutes - increased for PDF generation with many images
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - ADD AUTH HEADER FIRST
  client.interceptors.request.use(
    (config) => {
      const url = config.url || '';
      
      // Check if request should proceed (deduplication)
      if (!interceptRequest(url)) {
        return Promise.reject(new Error('Request blocked by deduplication'));
      }
      
      // Get auth token from localStorage (using consistent key name)
      const token = localStorage.getItem('auth_token');
      
      // Add Authorization header if token exists
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Ensure we're using HTTPS endpoints in production
      if (import.meta.env.PROD && config.url && !config.url.startsWith('http')) {
        // Make sure relative URLs use the HTTPS base
        config.baseURL = SAFE_API_BASE_URL;
      }
      
      const hasAuth = !!config.headers.Authorization;
      const fullURL = `${config.baseURL || SAFE_API_BASE_URL}${config.url}`;
      
      // Enhanced console logging for debugging
      console.log(`🔄 API Request: ${config.method?.toUpperCase()} ${fullURL}`, {
        hasAuth,
        baseURL: config.baseURL,
        token: hasAuth ? 'Present' : 'Missing',
        timestamp: new Date().toISOString()
      });
      
      // Also log to debug logger
      debugLogger.log('info', 'API Request', `${config.method?.toUpperCase()} ${url}`, {
        hasAuth,
        baseURL: config.baseURL,
        fullURL,
        timestamp: new Date().toISOString()
      });
      
      return config;
    },
    (error) => {
      debugLogger.log('error', 'API Request', 'Request interceptor error', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor - Enhanced error handling
  client.interceptors.response.use(
    (response) => {
      const fullURL = `${response.config.baseURL || SAFE_API_BASE_URL}${response.config.url}`;
      
      // Enhanced console logging for successful responses
      console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${fullURL}`, {
        status: response.status,
        statusText: response.statusText,
        timestamp: new Date().toISOString()
      });
      
      debugLogger.log('success', 'API Response', `${response.config.method?.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        statusText: response.statusText,
        timestamp: new Date().toISOString()
      });
      return response;
    },
    (error) => {
      const fullURL = `${error.config?.baseURL || SAFE_API_BASE_URL}${error.config?.url || ''}`;
      
      // Enhanced console logging for errors
      console.error(`❌ API Error: ${error.config?.method?.toUpperCase()} ${fullURL}`, {
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        data: error.response?.data,
        timestamp: new Date().toISOString()
      });
      
      // Use comprehensive error handler
      const handledError = handleAPIError(error);
      
      if (handledError && handledError.isHandled) {
        console.log(`🔧 Error handled gracefully: ${handledError.message}`);
        // Error was handled, create a more user-friendly error
        const enhancedError = new Error(handledError.message);
        (enhancedError as any).status = handledError.status;
        (enhancedError as any).endpoint = handledError.endpoint;
        (enhancedError as any).isHandled = true;
        return Promise.reject(enhancedError);
      }
      
      // For 400 errors that might be handled at component level, log with less verbosity
      if (error.response?.status === 400) {
        console.warn(`⚠️ API Warning: ${error.config?.method?.toUpperCase()} ${fullURL} - Bad Request (may be handled at component level)`);
      } else {
        // Log other errors normally
        debugLogger.log('error', 'API Error', `${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
          status: error.response?.status,
          statusText: error.response?.statusText,
          message: error.message,
          data: error.response?.data
        });
      }
      
      // Fallback to original error handling for unhandled cases
      if (error.response) {
        const { status, data, config } = error.response;
        debugLogger.log('error', 'API Response', `${status} ${config?.method?.toUpperCase()} ${config?.url}`, {
          status,
          statusText: error.response.statusText,
          data: data?.detail || data?.message || data,
          timestamp: new Date().toISOString()
        });
      } else if (error.request) {
        debugLogger.log('error', 'Network', 'No response received from server', {
          url: error.config?.url,
          method: error.config?.method,
          timestamp: new Date().toISOString()
        });
      } else {
        debugLogger.log('error', 'API Client', 'Request setup error', {
          message: error.message,
          timestamp: new Date().toISOString()
        });
      }
      
      return Promise.reject(error);
    }
  );  return client;
};

export const apiClient = createAPIClient();

// Base API class for common functionality
export class BaseAPI {
  protected client: AxiosInstance;

  constructor() {
    this.client = apiClient;
  }

  protected async request<T>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.request<T>(config);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const requestUrl = error.config?.url || '';
        const isPublicAuthFailure =
          error.response?.status === 401 &&
          [
            '/api/v1/auth/login',
            '/api/v1/auth/login-with-profile',
            '/api/v1/auth/register',
            '/api/v1/auth/forgot-password',
            '/api/v1/auth/resend-verification',
            '/api/v1/auth/verify-email',
            '/api/v1/auth/reset-password',
          ].some((endpoint) => requestUrl.includes(endpoint));

        // Session expiry only for authenticated API calls, not public auth failures
        if (error.response?.status === 401 && !isPublicAuthFailure) {
          localStorage.removeItem('auth_token');
          authEvents.emitSessionExpired();
          console.error('Authentication failed - redirecting to login');
          if (window.location.pathname !== '/auth/login') {
            window.location.href = '/auth/login';
          }
        }

        const detail =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          error.message;
        const enhancedError = new Error(
          typeof detail === 'string' ? detail : error.message
        );
        (enhancedError as any).response = error.response;
        (enhancedError as any).status = error.response?.status;
        (enhancedError as any).code = error.code;
        throw enhancedError;
      }
      throw error;
    }
  }

  // Raw request for binary/Blob responses
  protected async requestRaw(config: AxiosRequestConfig): Promise<any> {
    try {
      const response = await this.client.request(config);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // When responseType is 'blob', error response data is also a Blob - read it
        if (error.response?.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const json = JSON.parse(text);
            throw new Error(json.detail || json.message || error.message);
          } catch (parseError) {
            // If parsing fails, fall through to default error
          }
        }
        throw new Error(error.response?.data?.message || error.message);
      }
      throw error;
    }
  }
}
